import { makeAutoObservable, runInAction } from 'mobx';
import createStoreContext from '../createStoreContext';
import { UserApiResponse, TotalUsersCountApiResponse } from 'src/types/user';
import {
  getUserInteractionCountForMonth,
  getTotalActiveSpacesIntegrated,
  getTotalUsersAuthorized,
  getOrganizationInteractionCountForMonth,
  getUserInteractionDataForSelectedDates,
  getOrgInteractionDataForSelectedDates,
  getSlashCommandsCount,
  getMemoriesCountByOrgId,
  getTasksCountByOrgId,
  getMostActiveUsers,
  getMostActiveSpaces,
} from 'src/services/overview';
import {
  TotalInteractionCountApiResponse,
  TotalSpacesCountApiResponse,
  InteractionCountByDay,
  SlashCommandsCountsResponse,
  TotalCountApiResponse,
  UsageCountByUser,
  UsageCountBySpace,
} from 'src/types/overview';
import { ApiResponse } from 'src/types/commonResponseTypes';
import { OVERVIEW_CACHE_TTL_MS } from 'src/constants/overview';

type CacheEntry<T> = { data: T; lastFetchedAt: number };

export const [OverviewStoreProvider, useOverviewStore] = createStoreContext(
  class OverviewStore {
    userCurrentMonthInteractions: TotalInteractionCountApiResponse = {
      count: 0,
    };
    private cacheByKey: Record<string, CacheEntry<unknown>> = {};
    private inflightByKey: Record<
      string,
      Promise<ApiResponse<unknown>> | undefined
    > = {};

    public constructor() {
      makeAutoObservable(this);
    }

    private isCacheFresh(lastFetchedAt: number): boolean {
      return Date.now() - lastFetchedAt < OVERVIEW_CACHE_TTL_MS;
    }

    private getCacheEntry<T>(cacheKey: string): CacheEntry<T> | undefined {
      return this.cacheByKey[cacheKey] as CacheEntry<T> | undefined;
    }

    private setCacheEntry<T>(cacheKey: string, data: T) {
      this.cacheByKey[cacheKey] = {
        data,
        lastFetchedAt: Date.now(),
      };
    }

    private async fetchWithCache<T>(
      cacheKey: string,
      fetcher: () => Promise<ApiResponse<T>>,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<T>> {
      const cached = this.getCacheEntry<T>(cacheKey);

      if (cached && !options.force && this.isCacheFresh(cached.lastFetchedAt)) {
        return { success: true, content: cached.data };
      }

      if (!options.force && this.inflightByKey[cacheKey]) {
        return (await this.inflightByKey[cacheKey]) as ApiResponse<T>;
      }

      const inflight = (async () => {
        const response = await fetcher();

        if (response.success && response.content) {
          this.setCacheEntry(cacheKey, response.content);

          return response;
        }

        if (cached) {
          return { success: true, content: cached.data };
        }

        return response;
      })();

      this.inflightByKey[cacheKey] = inflight as Promise<ApiResponse<unknown>>;

      try {
        return await inflight;
      } finally {
        delete this.inflightByKey[cacheKey];
      }
    }

    private getMonthKey(date: Date): string {
      const month = String(date.getMonth() + 1).padStart(2, '0');

      return `${date.getFullYear()}-${month}`;
    }

    fetchCurrentMonthUserInteractions = async (
      userDetails: UserApiResponse | undefined,
      options: { force?: boolean } = {},
    ) => {
      if (!userDetails) {
        return null;
      }

      const now = new Date();
      const cacheKey = `user-interactions::${userDetails._id ?? 'unknown-user'}::${userDetails.organizationId ?? 'unknown-org'}::${this.getMonthKey(
        now,
      )}`;
      const response = await this.fetchWithCache(
        cacheKey,
        () => getUserInteractionCountForMonth(now.toString()),
        options,
      );

      if (response.success && response.content) {
        const interactions =
          response.content as TotalInteractionCountApiResponse;

        runInAction(() => {
          this.userCurrentMonthInteractions = interactions;
        });
      }

      return response.content ?? null;
    };

    fetchTotalActiveSpacesIntegrated = async (
      organizationId: string | undefined,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<TotalSpacesCountApiResponse>> => {
      const cacheKey = `total-active-spaces::${organizationId ?? 'unknown-org'}`;

      return this.fetchWithCache(
        cacheKey,
        getTotalActiveSpacesIntegrated,
        options,
      );
    };

    fetchTotalUsersAuthorized = async (
      organizationId: string | undefined,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<TotalUsersCountApiResponse>> => {
      const cacheKey = `total-users-authorized::${organizationId ?? 'unknown-org'}`;

      return this.fetchWithCache(cacheKey, getTotalUsersAuthorized, options);
    };

    fetchOrganizationInteractionCountForMonth = async (
      organizationId: string | undefined,
      currentDate: string,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<TotalInteractionCountApiResponse>> => {
      const cacheKey = `org-interactions-month::${organizationId ?? 'unknown-org'}::${this.getMonthKey(
        new Date(currentDate),
      )}`;

      return this.fetchWithCache(
        cacheKey,
        () => getOrganizationInteractionCountForMonth(currentDate),
        options,
      );
    };

    fetchUserInteractionDataForSelectedDates = async (
      userId: string | undefined,
      startDate: string,
      endDate: string,
      timeZoneOffset: string,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<InteractionCountByDay[]>> => {
      const cacheKey = `user-interactions-range::${userId ?? 'unknown-user'}::${startDate}::${endDate}::${timeZoneOffset}`;

      return this.fetchWithCache(
        cacheKey,
        () =>
          getUserInteractionDataForSelectedDates(
            startDate,
            endDate,
            timeZoneOffset,
          ),
        options,
      );
    };

    fetchOrgInteractionDataForSelectedDates = async (
      organizationId: string | undefined,
      startDate: string,
      endDate: string,
      timeZoneOffset: string,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<InteractionCountByDay[]>> => {
      const cacheKey = `org-interactions-range::${organizationId ?? 'unknown-org'}::${startDate}::${endDate}::${timeZoneOffset}`;

      return this.fetchWithCache(
        cacheKey,
        () =>
          getOrgInteractionDataForSelectedDates(
            startDate,
            endDate,
            timeZoneOffset,
          ),
        options,
      );
    };

    fetchSlashCommandsCount = async (
      organizationId: string | undefined,
      startDate: string,
      endDate: string,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<SlashCommandsCountsResponse[]>> => {
      const cacheKey = `slash-commands-count::${organizationId ?? 'unknown-org'}::${startDate}::${endDate}`;

      return this.fetchWithCache(
        cacheKey,
        () => getSlashCommandsCount(startDate, endDate),
        options,
      );
    };

    fetchMemoriesCountByOrgId = async (
      organizationId: string | undefined,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<TotalCountApiResponse>> => {
      const cacheKey = `memories-count::${organizationId ?? 'unknown-org'}`;

      return this.fetchWithCache(cacheKey, getMemoriesCountByOrgId, options);
    };

    fetchTasksCountByOrgId = async (
      organizationId: string | undefined,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<TotalCountApiResponse>> => {
      const cacheKey = `tasks-count::${organizationId ?? 'unknown-org'}`;

      return this.fetchWithCache(cacheKey, getTasksCountByOrgId, options);
    };

    fetchMostActiveUsers = async (
      organizationId: string | undefined,
      startDate: string,
      endDate: string,
      limit = 5,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<UsageCountByUser[]>> => {
      const cacheKey = `most-active-users::${organizationId ?? 'unknown-org'}::${startDate}::${endDate}::${limit}`;

      return this.fetchWithCache(
        cacheKey,
        () => getMostActiveUsers(startDate, endDate, limit),
        options,
      );
    };

    fetchMostActiveSpaces = async (
      organizationId: string | undefined,
      startDate: string,
      endDate: string,
      limit = 5,
      options: { force?: boolean } = {},
    ): Promise<ApiResponse<UsageCountBySpace[]>> => {
      const cacheKey = `most-active-spaces::${organizationId ?? 'unknown-org'}::${startDate}::${endDate}::${limit}`;

      return this.fetchWithCache(
        cacheKey,
        () => getMostActiveSpaces(startDate, endDate, limit),
        options,
      );
    };
  },
);

export type OverviewStore = ReturnType<typeof useOverviewStore>;
